// Connect WhatsApp page v2 (WP-06).
// Displays high-level stats, token expiry warning, WABA-grouped account cards,
// phone numbers table with health badges, limits, and actions.
// Preserves all existing flows: Embedded Signup (coexistence + cloud API), manual credentials,
// webhook inspection, OTP verification, and default number setting.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  RefreshIcon,
  PlusIcon,
  ConnectIcon,
} from '../components/icons';
import {
  whatsappConnectSummary,
  whatsappNumberHealth,
  whatsappSetIntegrationPaused,
  setDefaultIntegration,
  removeIntegration,
  type ConnectedNumber,
  type ConnectSummary,
  numberLabel,
} from '../lib/whatsappApi';
import { useAccounts } from '../lib/accounts';
import { isTokenExpiringSoon } from '../lib/connectHelpers';
import {
  ConnectStatCards,
  TokenExpiryBanner,
  WabaAccountCard,
  ConnectModal,
  NumberDetailsDrawer,
} from '../components/connect';

export default function Connect() {
  const { t } = useI18n();
  const accounts = useAccounts();
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ConnectSummary | null>(null);
  const [integrations, setIntegrations] = useState<ConnectedNumber[]>([]);

  // Modals / Drawers state
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [targetWabaId, setTargetWabaId] = useState<string | undefined>(undefined);
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  const [selectedNumberId, setSelectedNumberId] = useState<string | null>(null);

  // Busy states for specific actions
  const [busyNumberId, setBusyNumberId] = useState<string | null>(null);
  const [refreshingAll, setRefreshingAll] = useState(false);

  // Load summary and all integrations
  const loadData = useCallback(async () => {
    try {
      const res = await whatsappConnectSummary({});
      if (res.ok) {
        setSummary(res.summary);
        setIntegrations(res.integrations || []);
      }
    } catch (err) {
      toast.error((err as Error).message || t('connect.error'));
    } finally {
      setLoading(false);
    }
    // `toast` deliberately excluded: an error toast must not re-run the loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Group integrations by WABA (businessAccountId)
  const wabaGroups = useMemo(() => {
    const groups: Record<string, ConnectedNumber[]> = {};
    for (const item of integrations) {
      const wabaId = item.businessAccountId || 'unknown_waba';
      if (!groups[wabaId]) groups[wabaId] = [];
      groups[wabaId].push(item);
    }
    return groups;
  }, [integrations]);

  // Check if any token is expiring within 14 days
  const expiringCount = useMemo(() => {
    return integrations.filter((i) => isTokenExpiringSoon(i.health?.tokenExpiresAt)).length;
  }, [integrations]);

  // Selected number for details drawer
  const selectedNumber = useMemo(() => {
    return integrations.find((i) => i.id === selectedNumberId) || null;
  }, [integrations, selectedNumberId]);

  // Handler: Refresh health for single number
  async function handleRefreshSingleHealth(integrationId: string) {
    setBusyNumberId(integrationId);
    try {
      const res = await whatsappNumberHealth({ integrationId });
      if (res.ok && res.health) {
        const h = res.health;
        setIntegrations((prev) =>
          prev.map((item) =>
            item.id === integrationId
              ? { ...item, health: h, lastHealthError: h.error ?? null }
              : item
          )
        );
        toast.success(t('connect.refreshSuccess'));
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyNumberId(null);
    }
  }

  // Handler: Refresh all numbers health
  async function handleRefreshAllHealth() {
    setRefreshingAll(true);
    try {
      const res = await whatsappNumberHealth({});
      if (res.ok && res.items) {
        const healthMap = new Map(res.items.map((i) => [i.integrationId, i.health]));
        setIntegrations((prev) =>
          prev.map((item) => {
            const h = healthMap.get(item.id);
            return h ? { ...item, health: h, lastHealthError: h.error ?? null } : item;
          })
        );
        await loadData();
        toast.success(t('connect.refreshSuccess'));
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setRefreshingAll(false);
    }
  }

  // Handler: Toggle pause on number
  async function handleTogglePause(integrationId: string, currentlyPaused: boolean) {
    setBusyNumberId(integrationId);
    try {
      const res = await whatsappSetIntegrationPaused({
        integrationId,
        paused: !currentlyPaused,
      });
      if (res.ok) {
        setIntegrations((prev) =>
          prev.map((item) =>
            item.id === integrationId ? { ...item, isPaused: res.isPaused } : item
          )
        );
        await accounts.refresh();
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyNumberId(null);
    }
  }

  // Handler: Make number default
  async function handleMakeDefault(integrationId: string) {
    setBusyNumberId(integrationId);
    try {
      const res = await setDefaultIntegration({ integrationId });
      if (res.ok) {
        await accounts.refresh();
        await loadData();
        toast.success(t('accounts.defaultUpdated'));
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyNumberId(null);
    }
  }

  // Handler: Remove number
  async function handleRemove(number: ConnectedNumber) {
    const label = numberLabel(number);
    if (!(await confirm({
      title: t('connect.numbers.remove'),
      body: t('connect.numbers.removeConfirm', { n: label }),
      confirmLabel: t('connect.numbers.remove'),
      danger: true,
    }))) {
      return;
    }
    setBusyNumberId(number.id);
    try {
      const res = await removeIntegration({ integrationId: number.id });
      if (res.ok) {
        await accounts.refresh();
        await loadData();
        toast.success(t('connect.numbers.removed'));
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyNumberId(null);
    }
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Page Header */}
      <PageHeader
        title={t('connect.title')}
        subtitle={t('connect.subtitle')}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={refreshingAll || loading || integrations.length === 0}
              onClick={handleRefreshAllHealth}
            >
              <RefreshIcon size={14} className={refreshingAll ? 'animate-spin me-1.5' : 'me-1.5'} />
              {refreshingAll ? t('connect.refreshing') : t('connect.refreshAll')}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setTargetWabaId(undefined);
                setConnectModalOpen(true);
              }}
            >
              <PlusIcon size={14} className="me-1.5" />
              {t('connect.connectNew')}
            </Button>
          </div>
        }
      />

      {/* Token Expiry Warning Banner */}
      <TokenExpiryBanner
        expiringCount={expiringCount}
        onReconnect={() => {
          setTargetWabaId(undefined);
          setConnectModalOpen(true);
        }}
      />

      {/* Summary Stat Cards */}
      <ConnectStatCards summary={summary} loading={loading} />

      {/* WABA-Grouped Accounts Section */}
      <div className="space-y-4">
        <h2 className="text-base font-extrabold text-ink dark:text-slate-100">
          {t('connect.waba.title')}
        </h2>

        {loading ? (
          <div className="grid h-48 place-items-center rounded-card border border-line bg-white dark:border-slate-800 dark:bg-slate-900">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
          </div>
        ) : Object.keys(wabaGroups).length === 0 ? (
          <EmptyState
            icon={<ConnectIcon size={32} />}
            title={t('connect.empty.title')}
            description={t('connect.empty.description')}
            action={
              <Button
                size="sm"
                onClick={() => {
                  setTargetWabaId(undefined);
                  setConnectModalOpen(true);
                }}
              >
                <PlusIcon size={14} className="me-1.5" />
                {t('connect.connectNew')}
              </Button>
            }
          />
        ) : (
          Object.entries(wabaGroups).map(([wabaId, numbers]) => (
            <WabaAccountCard
              key={wabaId}
              businessAccountId={wabaId}
              numbers={numbers}
              defaultId={accounts.defaultId}
              manageId={selectedNumberId}
              busyId={busyNumberId}
              onManage={(id) => {
                setSelectedNumberId(id);
                setManageDrawerOpen(true);
              }}
              onRefreshHealth={handleRefreshSingleHealth}
              onMakeDefault={handleMakeDefault}
              onTogglePause={handleTogglePause}
              onRemove={handleRemove}
              onAddPhoneNumber={(targetWaba) => {
                setTargetWabaId(targetWaba);
                setConnectModalOpen(true);
              }}
            />
          ))
        )}
      </div>

      {/* Connect New Account / Phone Number Modal */}
      <ConnectModal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        prefilledWabaId={targetWabaId}
        onSuccess={async (newIntegrationId) => {
          await accounts.refresh();
          await loadData();
          if (newIntegrationId) {
            setSelectedNumberId(newIntegrationId);
          }
        }}
      />

      {/* Number Details & Configuration Drawer */}
      <NumberDetailsDrawer
        open={manageDrawerOpen}
        onClose={() => setManageDrawerOpen(false)}
        number={selectedNumber}
        onRefreshHealth={handleRefreshSingleHealth}
        onUpdated={async () => {
          await accounts.refresh();
          await loadData();
        }}
      />
    </div>
  );
}
